import { useCallback, useEffect, useState } from "react";
import { useCustom, useCustomMutation } from "@refinedev/core";

import type { TabelIndeling } from "@fawkes/ui";

/**
 * De kolomindeling van één lijst, van deze gebruiker.
 *
 * <p>Niet iedereen leest een lijst hetzelfde: de een zoekt op datum, de ander op factuurnummer.
 * Wat iemand verschuift of breder maakt blijft daarom staan -- per gebruiker, en op elk apparaat
 * waarop hij inlogt, want het staat op de server en niet in deze browser.
 *
 * <p>De indeling gaat meteen de deur uit bij elke verschuiving. Dat is bewust: er is geen knop
 * "opslaan" voor je kolommen, en een verschuiving die pas na een omweg blijft hangen voelt kapot.
 * Lukt het wegschrijven niet, dan blijft het scherm staan zoals je het zette -- hooguit staat het
 * morgen weer als vanouds, en dat is te overzien tegenover een foutmelding over kolombreedtes.
 */
export function useTabelIndeling(tabelKey: string): {
  indeling: TabelIndeling | undefined;
  setIndeling: (next: TabelIndeling) => void;
} {
  // Refine wil een record-achtig type; een indeling is dat ook, maar de server mag leeg antwoorden
  // als deze gebruiker nog niets instelde -- vandaar de eigen afhandeling hieronder.
  const { data } = useCustom<Record<string, unknown>>({
    url: `tabel-indeling/${tabelKey}`,
    method: "get",
  });
  const { mutate } = useCustomMutation();
  const [eigen, setEigen] = useState<TabelIndeling | undefined>(undefined);

  // Wat de server heeft geldt tot de gebruiker zelf iets verschuift; daarna is het scherm leidend,
  // anders zou een trage GET de versleepte kolom terugduwen onder je handen.
  const vanServer = (data?.data ?? undefined) as TabelIndeling | undefined;
  useEffect(() => {
    if (vanServer && Object.keys(vanServer).length > 0) {
      setEigen((huidig) => huidig ?? vanServer);
    }
  }, [vanServer]);

  const setIndeling = useCallback(
    (next: TabelIndeling) => {
      setEigen(next);
      mutate({
        url: `tabel-indeling/${tabelKey}`,
        method: "put",
        values: next,
        successNotification: false,
        errorNotification: false,
      });
    },
    [mutate, tabelKey],
  );

  return { indeling: eigen, setIndeling };
}
